import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMetadata } from '@contexts/org-metadata';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  ErrorMessageConstant,
  ShipmentTypeKeys,
} from '@utility/constants/common';
import { DateUtility } from '@utility/date';
import SCMService from '@utility/services/scm';
import StorageProviderService from '@utility/services/storage-provider';
import SummaryService from '@utility/services/summary';
import SupplierService from '@utility/services/supplier';
import { ToastMessage } from '@utility/toast';

/*
 * The SCM board's columns (suppliers, purchase orders, shipments, predicted
 * shipments), the inventory valuation snapshots and the seasonality graph, on
 * react-query. Each hook keeps the shape its callers destructure.
 */

export const scmKeys = {
  all: ['scm'] as const,
  purchaseOrders: (status: string, summary: boolean) =>
    [...scmKeys.all, 'purchase-orders', status, { summary }] as const,
  shipments: (statuses: string, summary: boolean) =>
    [...scmKeys.all, 'shipments', statuses, { summary }] as const,
  shipmentPrediction: (summary: boolean) =>
    [...scmKeys.all, 'shipment-prediction', { summary }] as const,
  suppliers: (summary: boolean) =>
    [...scmKeys.all, 'suppliers', { summary }] as const,
  snapshotDates: () => [...scmKeys.all, 'snapshot-dates'] as const,
  predictionGraph: (upc: string) =>
    [...scmKeys.all, 'prediction-graph', upc] as const,
};

/* The legacy board reads these rows under its own types. */
// biome-ignore lint/suspicious/noExplicitAny: see above.
type Row = any;
type Page = { rows: Row[]; total: number };

/* A shared empty list, so callers that memoize on it see one reference. */
const NO_ROWS: Row[] = [];

const errorText = (error: unknown) =>
  error
    ? (error as { message?: string; error?: string }).message ||
      (error as { error?: string }).error ||
      ErrorMessageConstant.defaultAPIError
    : '';

function useIsSummaryView(): boolean {
  // @ts-expect-error - TS2339: Property 'isSummaryView' does not exist on type 'unknown'.
  const { isSummaryView } = useMetadata();
  return !!isSummaryView;
}

/**
 * A board column a page at a time, rows appended. The summary view answers
 * with everything at once, so it has a single page.
 */
function useBoardColumn(
  queryKey: readonly unknown[],
  fetchPage: (page: number) => Promise<Page>,
  { enabled = true, summary = false } = {},
) {
  const query = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    getNextPageParam: (last, pages) => {
      if (summary) return undefined;
      const loaded = pages.reduce((sum, page) => sum + page.rows.length, 0);
      return loaded < last.total ? pages.length + 1 : undefined;
    },
    enabled,
  });

  const rows = useMemo(
    () => query.data?.pages.flatMap(page => page.rows),
    [query.data],
  );
  const total = query.data?.pages.at(-1)?.total ?? '';

  const nextPage = useCallback(() => {
    if (query.hasNextPage && !query.isFetching) void query.fetchNextPage();
  }, [query.hasNextPage, query.isFetching, query.fetchNextPage]);

  return {
    rows,
    total,
    nextPage,
    hasMoreData: !!query.hasNextPage,
    loading: query.isFetching,
    error: errorText(query.error),
    refetch: query.refetch,
  };
}

/* The board edits a column in place and flags its own work in progress. */
function useLocalColumn(rows: Row[] | undefined, fetching: boolean) {
  const [data, setData] = useState<Row[]>([]);
  const [busy, setLoading] = useState(false);
  useEffect(() => {
    if (rows) setData(rows);
  }, [rows]);
  return { data, setData, loading: fetching || busy, setLoading };
}

/* What a line costs at its quantity: the price of the largest MOQ it reaches. */
function withQuantityAndPrice(line: Row) {
  const qty = (line.aliases || []).reduce(
    (sum, alias) => sum + (alias?.qty || 0),
    0,
  );
  const price =
    [...(line.prices || [])]
      .sort((a, b) => b.moq - a.moq)
      .find(tier => tier.moq <= qty)?.price || 0;
  return { ...line, qty, price };
}

const sum = (lines: Row[]) =>
  lines.reduce(
    (totals, line) => ({
      qty: totals.qty + line.qty,
      amount: totals.amount + line.qty * line.price,
    }),
    { qty: 0, amount: 0 },
  );

function toPurchaseOrderRow(order: Row) {
  const items = (order.items || []).map(withQuantityAndPrice);
  const components = (order.components || []).map(line => ({
    ...withQuantityAndPrice(line),
    image: line?.images?.[0],
  }));
  const custom = sum(order.custom_items || []);
  const itemTotals = sum(items);
  const componentTotals = sum(components);

  return {
    ...order,
    items,
    components,
    supplier: order?.supplier?.supplier_name ?? order?.supplier ?? '',
    total_items: itemTotals.qty + componentTotals.qty + custom.qty,
    total_amount: itemTotals.amount + componentTotals.amount + custom.amount,
  };
}

/** Purchase orders in a status, with their quantities and totals worked out. */
export const scmPurchaseOrderHook = status => {
  const summary = useIsSummaryView();
  const column = useBoardColumn(
    scmKeys.purchaseOrders(status ?? '', summary),
    async page => {
      const response = summary
        ? await SummaryService.summaryPurchaseOrders(status)
        : await SCMService.purchaseOrders(status, {
            limit: CommonConstant.defaultPageSize,
            page,
          });
      return {
        rows: (response?.data ?? []).map(toPurchaseOrderRow),
        total: response?.total_data ?? 0,
      };
    },
    { enabled: !!status, summary },
  );

  return {
    data: column.rows ?? NO_ROWS,
    loading: column.loading,
    error: column.error,
    nextPage: column.nextPage,
    total: column.total,
    hasMoreData: column.hasMoreData,
  };
};

function toShipmentRow(shipment: Row) {
  return {
    ...shipment,
    organization_id:
      shipment?.organization?._id ?? shipment?.organization_id ?? '',
    supplier: shipment?.supplier?.supplier_name ?? shipment?.supplier ?? '',
    destination: shipment?.destination?.name ?? shipment?.destination ?? '',
    items: (shipment?.items || []).map(item => ({
      ...item,
      item_id: item.items_id ?? item._id ?? '',
    })),
    components: (shipment?.components || []).map(component => ({
      ...component,
      image: component.image?.[0],
    })),
  };
}

/** Shipments in the given statuses. `setRefresh` reloads every page loaded so far. */
export const scmShipmentHook = statusMetadata => {
  const summary = useIsSummaryView();
  const statuses = (statusMetadata || []).join(',');
  const column = useBoardColumn(
    scmKeys.shipments(statuses, summary),
    async page => {
      const response = summary
        ? await SummaryService.summaryShipments(statuses)
        : await SCMService.shipments(statuses, {
            limit: CommonConstant.defaultPageSize,
            page,
          });
      return {
        rows: (response?.data ?? []).map(toShipmentRow),
        total: response?.total_data ?? 0,
      };
    },
    { enabled: !!statuses, summary },
  );
  const local = useLocalColumn(column.rows, column.loading);

  return {
    data: local.data,
    loading: local.loading,
    error: column.error,
    nextPage: column.nextPage,
    hasMoreData: column.hasMoreData,
    setRefresh: (_key?: unknown) => void column.refetch(),
    setLoading: local.setLoading,
    setData: local.setData,
    total: column.total,
  };
};

/*
 * Predicted shipments, latest first. A purchase order's shipment groups with
 * those of other suppliers bound for the same destination within three weeks.
 */
function groupPredictions(result: Row[]) {
  const rows = result
    .map(prediction => {
      const { items, origins } = prediction;
      const supplierId = origins[0]._id;
      const byArrival = [...items].sort((a, b) =>
        a?.ETA < b?.ETA ? -1 : a?.ETA > b?.ETA ? 1 : 0,
      );
      /* Earliest arrival first, as the rows show their items. */
      return {
        ...prediction,
        items: byArrival,
        ETA: byArrival[0]?.ETA,
        daysLeft: byArrival[0]?.ETD
          ? DateUtility.difference(new Date(), new Date(byArrival[0].ETD))
          : 0,
        total_items: items.length,
        outOfStock: items.filter(item => item.outOfStock).length,
        runningLow: items.filter(item => item.runningLow).length,
        supplier_name: origins[0].name,
        supplier_id: supplierId,
        key: supplierId + items.length,
      };
    })
    .sort((a, b) => (a?.ETA < b?.ETA ? 1 : a?.ETA > b?.ETA ? -1 : 0));

  const clubbed: string[] = [];
  return rows.map(row => {
    if (row.origin_type !== ShipmentTypeKeys.PO) return row;
    if (clubbed.includes(row.supplier_id)) return { ...row, clubbed: true };

    const similar = rows.filter(other => {
      if (
        other.destination !== row.destination ||
        other.supplier_id === row.supplier_id
      ) {
        return false;
      }
      const days = Math.abs(
        Math.trunc(
          (new Date(other.ETA).getTime() - new Date(row.ETA).getTime()) /
            (1000 * 3600 * 24),
        ),
      );
      return days < 22;
    });
    if (!similar.length) return row;

    clubbed.push(...similar.map(other => other.supplier_id));
    return {
      ...row,
      grouped: similar,
      allItems: [
        ...(row?.items || []),
        ...similar.flatMap(other => other?.items || []),
      ],
      allSupplierIds: [
        row.supplier_id,
        ...similar.map(other => other.supplier_id),
      ],
    };
  });
}

export const scmShipmentPredictionHook = () => {
  const summary = useIsSummaryView();
  const query = useQuery({
    queryKey: scmKeys.shipmentPrediction(summary),
    queryFn: async () => {
      const response = summary
        ? await SummaryService.summaryGetShipmentPrediction()
        : await SCMService.getShipmentPrediction();
      const result = CommonUtility.isValidArray(response?.data)
        ? response.data
        : CommonUtility.isValidArray(response)
          ? response
          : [];
      return groupPredictions(result);
    },
  });

  return {
    data: query.data ?? NO_ROWS,
    loading: query.isFetching,
    error: errorText(query.error),
  };
};

/** Suppliers with their item counts, running low, out of stock and predicted cost. */
export const scmSupplierHook = () => {
  const summary = useIsSummaryView();
  const column = useBoardColumn(
    scmKeys.suppliers(summary),
    async page => {
      const result = summary
        ? await SummaryService.summaryScmSupplier()
        : await SupplierService.scm({
            limit: CommonConstant.defaultPageSize,
            page,
          });
      return {
        rows: (result?.data ?? []).map(supplier => ({
          ...supplier,
          total_items: supplier.supplier_items_count,
          runningLow: supplier.running_low_count || 0,
          outOfStock: supplier.out_of_stock_count || 0,
          totalPredictedCost: supplier.total_predicted_cost || 0,
        })),
        total: result?.total_data ?? 0,
      };
    },
    { summary },
  );

  return {
    data: column.rows ?? NO_ROWS,
    loading: column.loading,
    error: column.error,
    fetchData: () => void column.refetch(),
    hasMoreData: column.hasMoreData,
    nextPage: column.nextPage,
    total: column.total,
  };
};

/** The days an inventory valuation snapshot was taken. */
export const useInventoryValuationSnapshotDates = () => {
  const query = useQuery({
    queryKey: scmKeys.snapshotDates(),
    queryFn: async () => {
      const response =
        await StorageProviderService.fetchInventoryValuationSnapshotDates();
      return (
        CommonUtility.isValidArray(response?.data) ? response.data : []
      ) as Row[];
    },
  });

  return {
    isLoading: query.isFetching,
    data: query.data ?? NO_ROWS,
    error: query.error ? errorText(query.error) : null,
    fetchData: () => void query.refetch(),
  };
};

/** Downloads one day's inventory valuation snapshot as CSV. */
export const useInventoryValuationSnapshot = () => {
  const download = useMutation({
    mutationFn: async (date: string) => {
      const response =
        await StorageProviderService.fetchInventoryValuationSnapshot(date);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory-valuation-report-${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onSuccess: () =>
      ToastMessage.success(
        'The inventory valuation snapshot has been downloaded successfully.',
      ),
    onError: error => ToastMessage.apiError(error),
  });

  return {
    isLoading: download.isPending,
    fetchData: (date: string) =>
      download.mutateAsync(date).catch(() => undefined),
  };
};

/** The item's seasonality graph. A refresh asks the API to rebuild it. */
export const useScmPredictionGraph = (upc, refreshKey) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: scmKeys.predictionGraph(upc ?? ''),
    queryFn: async () => ({
      ...(await StorageProviderService.fetchScmPredictionGraph(upc, false)),
    }),
  });

  useEffect(() => {
    if (query.error) ToastMessage.apiError(query.error);
  }, [query.error]);

  const rebuild = useMutation({
    mutationFn: async () => ({
      ...(await StorageProviderService.fetchScmPredictionGraph(upc, true)),
    }),
    onSuccess: graph =>
      queryClient.setQueryData(scmKeys.predictionGraph(upc ?? ''), graph),
    onError: error => ToastMessage.apiError(error),
  });

  const refreshData = () => rebuild.mutate();

  useEffect(() => {
    if (CommonUtility.isValidNumber(refreshKey) && refreshKey > 0) {
      refreshData();
    }
  }, [refreshKey]);

  return {
    isLoading: query.isFetching || rebuild.isPending,
    data: query.data ?? null,
    refreshData,
  };
};
